/**
 * Chart theme for Record Flow, on Tide (docs/design/tide.md). Every colour and
 * marker comes from src/components/tide/tokens.ts, the one place category and
 * status colours are assigned, so a domain looks the same in a chart, its
 * legend, a table row and the resident timeline.
 */
import type { Domain, Origin } from './flowApi';
import {
  AGE, DOMAIN, ORIGIN, STAT, compareWords, wordToken as tideWordToken, tideAxisLine, tideAxisTick, tideGrid, tideTooltip,
} from '../../../components/tide';
import type { MarkerShape } from '../../../components/tide';

export const C = {
  foreground: 'var(--tide-ink)',
  muted: 'var(--tide-ink-2)',
  border: 'var(--tide-line)',
  card: 'var(--tide-surface)',
  median: STAT.median.color,
  tail: STAT.p90.color,
};

export const DOMAINS: Domain[] = ['PA', 'MANIFEST', 'UMED', 'FINANCE', 'COVERAGE', 'PROVIDER'];

export const DOMAIN_LABEL: Record<Domain, string> = {
  PA: DOMAIN.PA.label, MANIFEST: DOMAIN.MANIFEST.label, UMED: DOMAIN.UMED.label, FINANCE: DOMAIN.FINANCE.label, COVERAGE: DOMAIN.COVERAGE.label, PROVIDER: DOMAIN.PROVIDER.label,
};

export const DOMAIN_COLOR: Record<Domain, string> = {
  PA: DOMAIN.PA.color, MANIFEST: DOMAIN.MANIFEST.color, UMED: DOMAIN.UMED.color, FINANCE: DOMAIN.FINANCE.color, COVERAGE: DOMAIN.COVERAGE.color, PROVIDER: DOMAIN.PROVIDER.color,
};

export const DOMAIN_MARKER: Record<Domain, MarkerShape> = {
  PA: DOMAIN.PA.marker, MANIFEST: DOMAIN.MANIFEST.marker, UMED: DOMAIN.UMED.marker, FINANCE: DOMAIN.FINANCE.marker, COVERAGE: DOMAIN.COVERAGE.marker, PROVIDER: DOMAIN.PROVIDER.marker,
};

/** One sentence per domain, shown in legends (not hover-only). */
export const DOMAIN_NOTE: Record<Domain, string> = {
  PA: 'PA cases: stage, owner and handoff steps.',
  MANIFEST: 'Manifest decisions, lead intake, resubmittals and replacements.',
  UMED: 'UMED case movement, from creation to approval or reversal.',
  FINANCE: 'Finance handoffs to and from UMED.',
  COVERAGE: 'Which advocate covers a facility.',
  PROVIDER: 'Provider visits planned, confirmed, completed; check-ins; provider coverage and login links.',
};

export const ORIGINS: Origin[] = ['PERSON', 'SYSTEM', 'IMPORT'];

export const ORIGIN_LABEL: Record<Origin, string> = { PERSON: ORIGIN.PERSON.label, SYSTEM: ORIGIN.SYSTEM.label, IMPORT: ORIGIN.IMPORT.label };

export const ORIGIN_COLOR: Record<Origin, string> = { PERSON: ORIGIN.PERSON.color, SYSTEM: ORIGIN.SYSTEM.color, IMPORT: ORIGIN.IMPORT.color };

export const ORIGIN_MARKER: Record<Origin, MarkerShape> = { PERSON: ORIGIN.PERSON.marker, SYSTEM: ORIGIN.SYSTEM.marker, IMPORT: ORIGIN.IMPORT.marker };

export const ORIGIN_NOTE: Record<Origin, string> = {
  PERSON: 'A signed-in person acted in eMerge.',
  SYSTEM: 'eMerge automation, with no person.',
  IMPORT: 'Monday sync and backfill, not people working in eMerge.',
};

/** Time in the current stage. Older is redder. */
export const AGING_BUCKETS = [
  { key: 'd0_1', label: AGE.d0_1.label, color: AGE.d0_1.color },
  { key: 'd1_3', label: AGE.d1_3.label, color: AGE.d1_3.color },
  { key: 'd3_7', label: AGE.d3_7.label, color: AGE.d3_7.color },
  { key: 'd7_14', label: AGE.d7_14.label, color: AGE.d7_14.color },
  { key: 'd14_30', label: AGE.d14_30.label, color: AGE.d14_30.color },
  { key: 'd30p', label: AGE.d30p.label, color: AGE.d30p.color },
] as const;

/** Open records with no recorded stage-entry time: grey and hatched, because unknown is not young. */
export const UNAGED = {
  key: 'unaged', label: AGE.unknown.label, color: AGE.unknown.color, marker: AGE.unknown.marker,
  note: 'no recorded stage-entry time (mostly imported from Monday)',
} as const;

/** Cleanup status words: a fixed colour + glyph per known word, a hashed fallback (glyph "?X") for an unknown one. */
export const wordToken = (word: string) => tideWordToken(word);
/** Known words in vocabulary order first, unknown words after. */
export const compareWordOrder = compareWords;

export const axisTick = tideAxisTick;
export const axisLine = tideAxisLine;
export const gridProps = tideGrid;
export const tooltipProps = tideTooltip;

/** For charts whose data never changes in place (no legend control): nothing to animate. */
export const STATIC = { isAnimationActive: false } as const;
